export const HOME_REVISION = "organization-growth-2026-10-20";

export type OrganizationJourneyContext = {
  audience: "organization";
  home_revision?: string;
};

import {
  getGrowthJourneyContext,
  getJourneyCampaignContext,
  getJourneyId,
} from "./growthJourney";
export { getJourneyCampaignContext } from "./growthJourney";

const SAFE_VALUE = /^[a-zA-Z0-9_-]{1,150}$/;
const PENDING_LIBRARY_JOURNEY_KEY = "technical-leaders:library-journey:v1";
const PENDING_LIBRARY_JOURNEY_MAX_AGE_MS = 10 * 60 * 1000;

export function getOrganizationJourneyContext(search: string): OrganizationJourneyContext | null {
  const params = new URLSearchParams(search);
  if (params.get("audience") !== "organization") return null;
  const revision = getJourneyHomeRevision(search);
  return {
    audience: "organization",
    ...(revision ? { home_revision: revision } : {}),
  };
}

export function getJourneyHomeRevision(search: string) {
  const revision = new URLSearchParams(search).get("home_revision");
  return revision && SAFE_VALUE.test(revision) ? revision : undefined;
}

type JourneyDestination = {
  source: string;
  location: string;
  hash?: string;
  audience?: "organization";
  homeRevision?: string;
};

// Carry public campaign and buyer context through our own links. Do not copy
// arbitrary query data, which could include personal information or redirects.
export function buildOrganizationJourneyUrl(
  pathname: string,
  search: string,
  options: JourneyDestination,
) {
  const [pathAndQuery, existingHash = ""] = pathname.split("#", 2);
  const [path, query = ""] = pathAndQuery.split("?", 2);
  const params = new URLSearchParams(query);
  const incoming = new URLSearchParams(search);
  for (const [key, value] of Object.entries(getGrowthJourneyContext(search))) params.set(key, value);
  const workflow = incoming.get("workflow");
  if (workflow && SAFE_VALUE.test(workflow)) params.set("workflow", workflow);
  params.set("source", options.source);
  params.set("location", options.location);

  const organization = getOrganizationJourneyContext(search);
  const isTeamOffer = ["/ai-30", "/ai-30/summary", "/ai-for-leaders", "/ai-for-leaders/summary"].includes(path);
  if (isTeamOffer) {
    params.set("audience", "organization");
  } else if (options.audience === "organization" || organization) {
    params.set("audience", "organization");
  }
  // Revision describes the homepage treatment, independently of the buyer's
  // subsequent team offer or organizational support.
  const revision = options.homeRevision || getJourneyHomeRevision(search);
  if (revision && SAFE_VALUE.test(revision)) params.set("home_revision", revision);
  const hash = options.hash ?? existingHash;
  return `${path}?${params}${hash ? `#${hash.replace(/^#/, "")}` : ""}`;
}

// Kit's saved form redirects to a fixed confirmation URL. Save only public
// buyer context after Kit confirms success, scoped to that destination and tab.
export function rememberLibraryConfirmationJourney(
  context: OrganizationJourneyContext | null,
  confirmationPath: string,
  search = "",
) {
  if (typeof window === "undefined" || confirmationPath !== "/library-confirmed") return;
  try {
    window.sessionStorage.setItem(PENDING_LIBRARY_JOURNEY_KEY, JSON.stringify({
      ...(context || {}),
      journey: { ...getGrowthJourneyContext(search), ...(context || {}) },
      campaign: getJourneyCampaignContext(search),
      confirmationPath,
      createdAt: Date.now(),
    }));
  } catch {
    // Explicit URL context still works if storage is unavailable.
  }
}

export function getLibraryConfirmationJourney(
  search: string,
): ({ audience?: "organization"; home_revision?: string; campaign?: Record<string, string>; journey?: Record<string, string> }) | null {
  const incoming = new URLSearchParams(search);
  // An explicit individual choice always takes precedence over the short-lived
  // provider-redirect fallback.
  if (incoming.has("audience")) return getOrganizationJourneyContext(search);
  if (typeof window === "undefined" || window.location.pathname !== "/library-confirmed") return null;
  try {
    const raw = window.sessionStorage.getItem(PENDING_LIBRARY_JOURNEY_KEY);
    if (!raw) return null;
    const pending = JSON.parse(raw);
    if (
      pending.confirmationPath !== "/library-confirmed" ||
      typeof pending.createdAt !== "number" ||
      Date.now() < pending.createdAt ||
      Date.now() - pending.createdAt > PENDING_LIBRARY_JOURNEY_MAX_AGE_MS
    ) {
      window.sessionStorage.removeItem(PENDING_LIBRARY_JOURNEY_KEY);
      return null;
    }
    const context = getOrganizationJourneyContext(new URLSearchParams({
      audience: pending.audience === "organization" ? "organization" : "individual",
      ...(typeof pending.home_revision === "string" ? { home_revision: pending.home_revision } : {}),
    }).toString());
    const campaignSearch = new URLSearchParams(
      typeof pending.campaign === "object" && pending.campaign ? pending.campaign : {},
    ).toString();
    const storedJourney = typeof pending.journey === "object" && pending.journey ? pending.journey : {};
    const journey = getGrowthJourneyContext(new URLSearchParams(storedJourney).toString());
    const id = getJourneyId(search);
    if (id) journey.journey_id = id;
    const campaign = getJourneyCampaignContext(campaignSearch);
    if (!context && !Object.keys(campaign).length && !Object.keys(journey).length) return null;
    return { ...(context || {}), campaign, journey };
  } catch {
    return null;
  }
}
